import type { components } from '@leanin/api-client';
import { getApiClient } from '@/lib/api-client';
import { throwApiError } from '@/lib/api-error';
import type { OnboardingPersona } from './persona';

// Token stashed in sessionStorage by the SignupForm when the user arrives
// via /invites/:token. Read once by the onboarding flow to pre-route
// persona. The invited group's id is NOT stashed here — by the time the
// onboarding screens need it, the Clerk webhook has redeemed the invite
// and `GET /me/onboarding-progress` returns `leaderCircleId` /
// `leaderNetworkId` server-side. Server is the source of truth.
const PENDING_INVITE_TOKEN_KEY = 'onboarding.pendingInviteToken';

export function writePendingInviteToken(token: string): void {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(PENDING_INVITE_TOKEN_KEY, token);
}

export function readPendingInviteToken(): string | null {
  if (typeof window === 'undefined') return null;
  return window.sessionStorage.getItem(PENDING_INVITE_TOKEN_KEY);
}

export function clearPendingInviteToken(): void {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(PENDING_INVITE_TOKEN_KEY);
}

export type InvitePreview = components['schemas']['InvitePreview'];

// Maps an invite preview to the onboarding persona it implies. Leaders and
// circle members are pre-routed to the role-specific tour. Network members
// see the persona selector — they were invited into the community but may
// want to lead a Circle or take a different path.
export function personaFromInvitePreview(
  preview: InvitePreview,
): OnboardingPersona | null {
  if (preview.status !== 'active') return null;
  const { kind } = preview.group;
  const role = preview.roleOnAccept;
  if (kind === 'circle' && role === 'leader') return 'circle-leader';
  if (kind === 'network' && role === 'leader') return 'network-leader';
  if (kind === 'circle' && role === 'member') return 'circle-member';
  // Network member / space member → persona selector.
  return null;
}

export async function fetchInvitePreview(token: string): Promise<InvitePreview | null> {
  const client = getApiClient();
  const { data, response } = await client.GET('/api/v1/invites/{token}', {
    params: { path: { token } },
  });
  if (response.status === 404) return null;
  if (data === undefined) return null;
  return data;
}

export type InviteMagicLinkResult = components['schemas']['InviteMagicLinkResponse'];

// Magic-link redemption: server mints a one-shot Clerk auth artifact
// derived from the invite token. The frontend at /invites/$token
// consumes the result based on `kind`.
//   - `signin` → `signIn.create({ strategy: 'ticket', ticket: signInToken })`
//   - `signup` → redirect the browser to `redirectUrl` (Clerk-hosted
//                ticket exchange that forwards to our /auth/signup)
// Throws the canonical `DomainError` (server code at `.error.code`) on
// non-201, exactly like the rest of the api-client layer — so callers can
// read the code via `failureFromError`. A bare `Error(code)` stashed the
// code in `.message`, which that extractor never reads; every magic-link
// failure then collapsed to `UNKNOWN` and the /invites/$token fallback
// bounced the visitor to /auth/login regardless of the real cause.
export async function requestInviteMagicLink(
  token: string,
): Promise<InviteMagicLinkResult> {
  const client = getApiClient();
  const { data, error, response } = await client.POST(
    '/api/v1/invites/{token}/magic-link',
    { params: { path: { token } } },
  );
  if (error !== undefined || data === undefined) {
    throwApiError(error, response);
  }
  return data;
}
